import React, { memo } from "react";
import {
  Button,
  HStack,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import { GroupMembershipIcon } from "modules/icons";
import { useOverlay } from "modules/providers/overlay-provider";
import colors from "app/colors";
import { useQuery } from "@apollo/client";
import { RuleGroupType } from "react-querybuilder";
import {
  AdminPreviewDistributionGroupMembershipInput,
  InternalAdminPreviewDistributionGroupMembershipQuery,
  InternalAdminPreviewDistributionGroupMembershipQueryVariables,
} from "gql/graphql";
import INTERNAL_ADMIN_PREVIEW_DISTRIBUTION_GROUP_MEMBERSHIP_QUERY from "../_networking/internal-admin-preview-distribution-group-user-id-list-query";
import useQueryString from "modules/hooks/use-query-string";
import useInternalAdminUsers from "../hooks/use-internal-admin-users";
import useInternalAdminDistributionGroups from "../hooks/use-internal-admin-distribution-groups";
import InternalAdminDistributionGroupPreviewUsersAndGroups from "../distribution-groups/internal-admin-distribution-group-preview-users-and-groups";
import InternalAdminDistributionGroupMemberList from "../distribution-groups/internal-admin-distribution-group-member-list";

interface IProps {
  adminPreviewDistributionGroupMembershipInput: AdminPreviewDistributionGroupMembershipInput;
  query?: RuleGroupType;
}

const InternalAdminDistributionGroupPreviewUsersModal = ({
  adminPreviewDistributionGroupMembershipInput,
  query,
}: IProps) => {
  const { closeModal } = useOverlay();
  const { qsOrganizationId } = useQueryString();

  const { data, loading } = useQuery<
    InternalAdminPreviewDistributionGroupMembershipQuery,
    InternalAdminPreviewDistributionGroupMembershipQueryVariables
  >(INTERNAL_ADMIN_PREVIEW_DISTRIBUTION_GROUP_MEMBERSHIP_QUERY, {
    variables: qsOrganizationId
      ? {
          organizationId: qsOrganizationId,
          input: adminPreviewDistributionGroupMembershipInput,
        }
      : undefined,
    skip: !qsOrganizationId,
  });

  const { users: addedUsers } = useInternalAdminUsers(
    adminPreviewDistributionGroupMembershipInput.addedUserIdList ?? [],
  );

  const { users: emailUsers } = useInternalAdminUsers(
    adminPreviewDistributionGroupMembershipInput.addedEmailUserIdList ?? [],
  );

  const { distributionGroups } = useInternalAdminDistributionGroups(
    adminPreviewDistributionGroupMembershipInput.distributionGroupIdList,
  );

  const { users: previewUsers } = useInternalAdminUsers(
    data
      ? [...data.internalAdminPreviewDistributionGroupMembership.userIdList]
      : [],
  );

  const { users: previewEmailUsers } = useInternalAdminUsers(
    data
      ? [
          ...data.internalAdminPreviewDistributionGroupMembership
            .emailUserIdList,
        ]
      : [],
  );

  return (
    <Modal isOpen onClose={closeModal} size="2xl" isCentered>
      <ModalOverlay />
      <ModalContent borderRadius={16}>
        <ModalBody>
          <Stack w="full" spacing={4} p={6} alignItems="center">
            <VStack w="full" spacing={0}>
              <Text
                fontFamily="Outfit"
                fontWeight={700}
                fontSize={24}
                color={colors.blackSecondary}
              >
                Preview Group Membership
              </Text>
              <Text fontWeight={500} fontSize={14} color={colors.gray50}>
                All users who would currently be included in this group
              </Text>
            </VStack>
            <InternalAdminDistributionGroupPreviewUsersAndGroups
              addedUsers={addedUsers}
              distributionGroups={distributionGroups}
              emailUsers={emailUsers}
              query={query}
            />
            <VStack w="full" h="300px" spacing={0}>
              <HStack
                w="full"
                borderBottomWidth={1}
                borderColor={colors.gray30}
              >
                <GroupMembershipIcon boxSize={5} />
                <Text fontWeight={500} fontSize={14} color={colors.gray50}>
                  Total List of People in Group (
                  {loading ? (
                    <Spinner size="xs" />
                  ) : (
                    previewUsers.length + previewEmailUsers.length
                  )}
                  )
                </Text>
              </HStack>
              <InternalAdminDistributionGroupMemberList
                users={previewUsers}
                emailUsers={previewEmailUsers}
              />
            </VStack>
            <Button h={50} w={139} onClick={closeModal}>
              Close
            </Button>
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default memo(InternalAdminDistributionGroupPreviewUsersModal);
